import produce, { Draft } from "immer";
import { findIndex, sample, some } from "lodash";

import { BassHit, Drum, DrumHit, Hit, LARGE_SCREEN_DRUMS, LARGE_SCREEN_NOTES, LARGE_SCREEN_TIMESTEPS, Machine, Note, NOTES, SMALL_SCREEN_DRUMS, SMALL_SCREEN_NOTES, SMALL_SCREEN_TIMESTEPS, TB303ControlState, TB303State, TB303Waveform, TR808State } from "./core";

export type StartAction = { type: "start"; machine: Machine };
export type StopAction = { type: "stop"; machine: Machine };
export type ToggleHitAction = {
    type: "toggleHit";
    machine: Machine;
    timeStep: number;
    row: string;
    isFillHit?: boolean;
};
export type ClearHitsAction = {
    type: "clearHits";
    machine: Machine;
};
export type GenerateHitsAction = {
    type: "generateHits";
    machine: Machine & "tb303";
};
export type TriggerFill = { type: 'triggerFill' };
export type SetBpmAction = { type: "setBpm"; bpm: number };
export type SetShuffleAction = {
    type: "setShuffle";
    machine: Machine & "tr808";
    shuffle: number;
};
export type AdjustControlAction = {
    type: "adjustControl";
    machine: Machine & "tb303";
    control: keyof TB303ControlState;
    value: number;
};
export type SetWaveformAction = {
    type: "setWaveform";
    machine: Machine & "tb303";
    waveform: TB303Waveform;
};
export type ToggleAiVariationAction = {
    type: "toggleAiVariation";
    machine: Machine & "tr808";
    on: boolean;
}
export type SetAiVariationAction = {
    type: "setAiVariation";
    sequence: DrumHit[];
}
export type SwitchLayoutAction = {
    type: "switchLayout";
    isSmallScreen: boolean;
};

export type AppAction =
    | StartAction
    | StopAction
    | ToggleHitAction
    | ClearHitsAction
    | GenerateHitsAction
    | TriggerFill
    | SetBpmAction
    | SetShuffleAction
    | AdjustControlAction
    | SetWaveformAction
    | ToggleAiVariationAction
    | SetAiVariationAction
    | SwitchLayoutAction;

export type AppState = {
    readonly tr808: TR808State;
    readonly tb303: TB303State;
    readonly bpm: number;
};


export let appReducer = produce((state: Draft<AppState>, action: AppAction) => {
    switch (action.type) {
        case "start":
            state[action.machine].playing = true;
            break;
        case "stop":
            state[action.machine].playing = false;
            break;
        case "toggleHit":
            let seq = state[action.machine].sequence;
            if (action.machine === "tr808") {
                if (state.tr808.aiVariationOn) seq = state.tr808.aiVariationSequence;
                if (action.isFillHit) seq = state.tr808.fillSequence;
            }
            let previouslyOn = some(seq, (h: Hit<any>) => h.timeStep === action.timeStep && h.note === action.row);
            let on = !previouslyOn;
            if (on) {
                if (action.machine === "tr808") {
                    let dSeq = seq as DrumHit[];
                    dSeq.push({
                        type: "drum",
                        timeStep: action.timeStep,
                        note: action.row as Drum,
                    });
                } else {
                    let bSeq = seq as BassHit[];
                    for (let i = bSeq.length - 1; i >= 0; i--) {
                        if (bSeq[i].timeStep === action.timeStep) {
                            bSeq.splice(i, 1);
                        }
                    }
                    bSeq.push({
                        type: "bass",
                        timeStep: action.timeStep,
                        note: action.row as Note,
                        accent: getRandomBassAccent(action.timeStep),
                        octave: 2,
                        slide: getRandomBassSlide(),
                    });
                }
            } else {
                let idx = findIndex(
                    seq as Hit<any>[],
                    (n) => n.timeStep === action.timeStep && n.note === action.row
                );
                seq.splice(idx, 1);
            }
            break;
        case "clearHits":
            state[action.machine].sequence.length = 0;
            break;
        case "generateHits":
            state.tb303.sequence = generateBassSequence();
            break;
        case "triggerFill":
            state.tr808.fillSequence = generateFillSequence();
            state.tr808.fillCount++;
            break;
        case "setBpm":
            state.bpm = action.bpm;
            break;
        case "setShuffle":
            state.tr808.shuffle = action.shuffle;
            break;
        case "adjustControl":
            state.tb303.controls[action.control] = action.value;
            break;
        case "setWaveform":
            state.tb303.waveform = action.waveform;
            break;
        case "toggleAiVariation":
            if (action.on) {
                state.tr808.aiVariationOn = true;
                if (state.tr808.aiVariationSequence.length === 0) {
                    state.tr808.aiVariationSequence = [...state.tr808.sequence];
                }
            } else {
                state.tr808.aiVariationOn = false;
            }
            break;
        case "setAiVariation":
            if (state.tr808.aiVariationOn) {
                state.tr808.aiVariationSequence = action.sequence;
            }
            break;
        case "switchLayout":
            state.tb303.layout.timeSteps = action.isSmallScreen ? SMALL_SCREEN_TIMESTEPS : LARGE_SCREEN_TIMESTEPS;
            state.tr808.layout.timeSteps = action.isSmallScreen ? SMALL_SCREEN_TIMESTEPS : LARGE_SCREEN_TIMESTEPS;
            state.tb303.layout.rows = action.isSmallScreen ? SMALL_SCREEN_NOTES : LARGE_SCREEN_NOTES;
            state.tr808.layout.rows = action.isSmallScreen ? SMALL_SCREEN_DRUMS : LARGE_SCREEN_DRUMS;
            state.tb303.sequence = state.tb303.sequence.filter(n => n.timeStep < state.tb303.layout.timeSteps && state.tb303.layout.rows.indexOf(n.note) >= 0);
            state.tr808.sequence = state.tr808.sequence.filter(n => n.timeStep < state.tr808.layout.timeSteps && state.tr808.layout.rows.indexOf(n.note) >= 0);
            break;
    }
});

function generateBassSequence(): BassHit[] {
    let seq: BassHit[] = [];
    let density = getRandomBassDensity();
    let scale = [36, 38, 40, 41, 43, 45, 47];

    for (let i = 0; i < 16; i++) {
        if (randomiseBassNoteOn(i, density)) {
            let note = scale[Math.floor(Math.random() * scale.length)];
            let octave = getRandomBassOctave();
            let slide = getRandomBassSlide();

            let accent = getRandomBassAccent(i, density);

            seq.push({
                type: "bass",
                timeStep: i,
                note: NOTES[note % 12],
                octave,
                slide,
                accent,
            });
        }
    }

    return seq;
}

function getRandomBassDensity() {
    return 0.4 + Math.random() * 0.6;
}

function getRandomBassAccent(
    timeStep: number,
    density = getRandomBassDensity()
) {
    if (timeStep === 0) {
        return Math.random() > 0.5 ? true : false;
    } else {
        let rnd = Math.random();
        if (rnd < density / 3) {
            return true;
        } else {
            return false;
        }
    }
}

function randomiseBassNoteOn(
    timeStep: number,
    density = getRandomBassDensity()
) {
    if (timeStep === 0) {
        return true;
    } else {
        return Math.random() < density;
    }
}

function getRandomBassOctave() {
    let octave = 1;
    if (Math.random() < 0.2) octave += 1;
    return octave;
}

function getRandomBassSlide() {
    return Math.random() < 0.3;
}


export function makeInitialState(isSmallScreen: boolean): AppState {
    let timeSteps = isSmallScreen ? SMALL_SCREEN_TIMESTEPS : LARGE_SCREEN_TIMESTEPS;
    let drums = isSmallScreen ? SMALL_SCREEN_DRUMS : LARGE_SCREEN_DRUMS;
    let scale = isSmallScreen ? SMALL_SCREEN_NOTES : LARGE_SCREEN_NOTES;
    return {
        tr808: {
            playing: false,
            layout: {
                timeSteps,
                rows: drums,
            },
            sequence: makeInitDrumSeq(drums, timeSteps),
            aiVariationSequence: [],
            fillSequence: [],
            shuffle: 0,
            fillCount: 0,
            aiVariationOn: false
        },
        tb303: {
            playing: false,
            layout: {
                timeSteps: isSmallScreen ? SMALL_SCREEN_TIMESTEPS : LARGE_SCREEN_TIMESTEPS,
                rows: scale,
            },
            sequence: makeInitBassSeq(timeSteps),
            waveform: "sawtooth",
            controls: {
                cutOffFreq: 1500,
                resonance: 8.5,
                envMod: 0.5,
                decay: 0.5,
                accent: 0.5,
            },
        },
        bpm: 120,
    }
};

function makeInitDrumSeq(drums: Drum[], timeSteps: number) {
    return [
        { type: "drum", timeStep: 0, note: "BD" },
        { type: "drum", timeStep: 4, note: "BD" },
        { type: "drum", timeStep: 8, note: "BD" },
        { type: "drum", timeStep: 12, note: "BD" },

        { type: "drum", timeStep: 4, note: "SD" },
        { type: "drum", timeStep: 12, note: "SD" },

        { type: "drum", timeStep: 9, note: "LT" },
        { type: "drum", timeStep: 12, note: "LT" },
        { type: "drum", timeStep: 14, note: "LT" },

        { type: "drum", timeStep: 1, note: "MT" },
        { type: "drum", timeStep: 4, note: "MT" },

        { type: "drum", timeStep: 7, note: "HT" },
        { type: "drum", timeStep: 8, note: "HT" },
        { type: "drum", timeStep: 11, note: "HT" },
        { type: "drum", timeStep: 14, note: "HT" },

        { type: "drum", timeStep: 3, note: "RS" },
        { type: "drum", timeStep: 7, note: "RS" },
        { type: "drum", timeStep: 14, note: "RS" },

        { type: "drum", timeStep: 4, note: "CP" },
        { type: "drum", timeStep: 12, note: "CP" },

        { type: "drum", timeStep: 0, note: "CH" },
        { type: "drum", timeStep: 1, note: "CH" },
        { type: "drum", timeStep: 2, note: "CH" },
        { type: "drum", timeStep: 4, note: "CH" },
        { type: "drum", timeStep: 5, note: "CH" },
        { type: "drum", timeStep: 6, note: "OH" },
        { type: "drum", timeStep: 7, note: "CH" },
        { type: "drum", timeStep: 8, note: "CH" },
        { type: "drum", timeStep: 9, note: "CH" },
        { type: "drum", timeStep: 10, note: "OH" },
        { type: "drum", timeStep: 11, note: "CH" },
        { type: "drum", timeStep: 12, note: "CH" },
        { type: "drum", timeStep: 13, note: "CH" },
        { type: "drum", timeStep: 14, note: "CH" },
        { type: "drum", timeStep: 15, note: "CH" },

        { type: "drum", timeStep: 0, note: "AC" },
        { type: "drum", timeStep: 8, note: "AC" },
    ].filter(h => drums.indexOf(h.note as Drum) >= 0 && h.timeStep < timeSteps) as DrumHit[]
}

function makeInitBassSeq(timeSteps: number) {
    return [
        {
            type: "bass",
            timeStep: 0,
            note: "D#",
            slide: true,
            accent: false,
            octave: 1,
        },
        {
            type: "bass",
            timeStep: 1,
            note: "D#",
            slide: false,
            accent: false,
            octave: 2,
        },
        {
            type: "bass",
            timeStep: 2,
            note: "D#",
            slide: false,
            accent: true,
            octave: 1,
        },
        {
            type: "bass",
            timeStep: 3,
            note: "D#",
            slide: false,
            accent: false,
            octave: 1,
        },
        {
            type: "bass",
            timeStep: 4,
            note: "C",
            slide: false,
            accent: false,
            octave: 2,
        },
        {
            type: "bass",
            timeStep: 5,
            note: "D#",
            slide: false,
            accent: true,
            octave: 2,
        },
        {
            type: "bass",
            timeStep: 6,
            note: "D#",
            slide: false,
            accent: false,
            octave: 1,
        },
        {
            type: "bass",
            timeStep: 7,
            note: "D#",
            slide: true,
            accent: false,
            octave: 1,
        },
        {
            type: "bass",
            timeStep: 8,
            note: "A#",
            slide: false,
            accent: false,
            octave: 2,
        },
        {
            type: "bass",
            timeStep: 9,
            note: "F",
            slide: false,
            accent: false,
            octave: 1,
        },
        {
            type: "bass",
            timeStep: 10,
            note: "G",
            slide: false,
            accent: false,
            octave: 2,
        },
        {
            type: "bass",
            timeStep: 11,
            note: "C",
            slide: true,
            accent: false,
            octave: 2,
        },
        {
            type: "bass",
            timeStep: 12,
            note: "D#",
            slide: false,
            accent: false,
            octave: 2,
        },
        {
            type: "bass",
            timeStep: 13,
            note: "C",
            slide: false,
            accent: false,
            octave: 1,
        },
        {
            type: "bass",
            timeStep: 14,
            note: "F",
            slide: false,
            accent: true,
            octave: 1,
        },
        {
            type: "bass",
            timeStep: 15,
            note: "C",
            slide: false,
            accent: false,
            octave: 1,
        },
    ].filter(h => h.timeStep < timeSteps) as BassHit[];
}

function generateFillSequence(): DrumHit[] {
    return sample([
        [
            { type: "drum", timeStep: 0, note: "BD" },
            { type: "drum", timeStep: 3, note: "BD" },
            { type: "drum", timeStep: 7, note: "BD" },
            { type: "drum", timeStep: 11, note: "BD" },
            { type: "drum", timeStep: 13, note: "BD" },

            { type: "drum", timeStep: 0, note: "SD" },
            { type: "drum", timeStep: 1, note: "SD" },
            { type: "drum", timeStep: 2, note: "SD" },
            { type: "drum", timeStep: 4, note: "SD" },
            { type: "drum", timeStep: 5, note: "SD" },
            { type: "drum", timeStep: 7, note: "SD" },
            { type: "drum", timeStep: 8, note: "SD" },
            { type: "drum", timeStep: 9, note: "SD" },
            { type: "drum", timeStep: 11, note: "SD" },
            { type: "drum", timeStep: 12, note: "SD" },
            { type: "drum", timeStep: 15, note: "SD" },

            { type: "drum", timeStep: 13, note: "LT" },

            { type: "drum", timeStep: 3, note: "MT" },
            { type: "drum", timeStep: 4, note: "MT" },
            { type: "drum", timeStep: 10, note: "MT" },

            { type: "drum", timeStep: 6, note: "HT" },


            { type: "drum", timeStep: 1, note: "CP" },
            { type: "drum", timeStep: 5, note: "CP" },
            { type: "drum", timeStep: 7, note: "CP" },
            { type: "drum", timeStep: 10, note: "CP" },
            { type: "drum", timeStep: 14, note: "CP" },

            { type: "drum", timeStep: 0, note: "CH" },
            { type: "drum", timeStep: 2, note: "CH" },
            { type: "drum", timeStep: 4, note: "CH" },
            { type: "drum", timeStep: 6, note: "CH" },
            { type: "drum", timeStep: 8, note: "CH" },
            { type: "drum", timeStep: 9, note: "CH" },
            { type: "drum", timeStep: 10, note: "CH" },
            { type: "drum", timeStep: 11, note: "CH" },
            { type: "drum", timeStep: 12, note: "CH" },
            { type: "drum", timeStep: 13, note: "CH" },
            { type: "drum", timeStep: 14, note: "CH" },
            { type: "drum", timeStep: 15, note: "CH" }
        ] as DrumHit[],
        [
            { type: "drum", timeStep: 0, note: "BD" },
            { type: "drum", timeStep: 8, note: "BD" },

            { type: "drum", timeStep: 0, note: "SD" },
            { type: "drum", timeStep: 1, note: "SD" },
            { type: "drum", timeStep: 2, note: "SD" },
            { type: "drum", timeStep: 4, note: "SD" },
            { type: "drum", timeStep: 5, note: "SD" },
            { type: "drum", timeStep: 6, note: "SD" },
            { type: "drum", timeStep: 7, note: "SD" },
            { type: "drum", timeStep: 10, note: "SD" },
            { type: "drum", timeStep: 11, note: "SD" },
            { type: "drum", timeStep: 12, note: "SD" },
            { type: "drum", timeStep: 13, note: "SD" },
            { type: "drum", timeStep: 14, note: "SD" },


            { type: "drum", timeStep: 0, note: "RS" },
            { type: "drum", timeStep: 2, note: "RS" },
            { type: "drum", timeStep: 4, note: "RS" },
            { type: "drum", timeStep: 6, note: "RS" },
            { type: "drum", timeStep: 8, note: "RS" },
            { type: "drum", timeStep: 9, note: "RS" },
            { type: "drum", timeStep: 12, note: "RS" },
            { type: "drum", timeStep: 14, note: "RS" },
            { type: "drum", timeStep: 15, note: "RS" },

            { type: "drum", timeStep: 1, note: "CP" },
            { type: "drum", timeStep: 2, note: "CP" },
            { type: "drum", timeStep: 5, note: "CP" },
            { type: "drum", timeStep: 9, note: "CP" },
            { type: "drum", timeStep: 12, note: "CP" },
            { type: "drum", timeStep: 15, note: "CP" },

            { type: "drum", timeStep: 0, note: "CY" },

            { type: "drum", timeStep: 4, note: "OH" },
            { type: "drum", timeStep: 7, note: "OH" },
            { type: "drum", timeStep: 9, note: "OH" },
            { type: "drum", timeStep: 12, note: "OH" },

        ] as DrumHit[],
        [
            { type: "drum", timeStep: 0, note: "BD" },
            { type: "drum", timeStep: 3, note: "BD" },
            { type: "drum", timeStep: 6, note: "BD" },
            { type: "drum", timeStep: 10, note: "BD" },
            { type: "drum", timeStep: 14, note: "BD" },

            { type: "drum", timeStep: 0, note: "CP" },
            { type: "drum", timeStep: 1, note: "CP" },
            { type: "drum", timeStep: 2, note: "CP" },
            { type: "drum", timeStep: 4, note: "CP" },
            { type: "drum", timeStep: 6, note: "CP" },
            { type: "drum", timeStep: 8, note: "CP" },
            { type: "drum", timeStep: 10, note: "CP" },
            { type: "drum", timeStep: 11, note: "CP" },
            { type: "drum", timeStep: 13, note: "CP" },
            { type: "drum", timeStep: 14, note: "CP" },

            { type: "drum", timeStep: 0, note: "CY" },

            { type: "drum", timeStep: 0, note: "OH" },
            { type: "drum", timeStep: 3, note: "OH" },
            { type: "drum", timeStep: 6, note: "OH" },
            { type: "drum", timeStep: 10, note: "OH" },
            { type: "drum", timeStep: 14, note: "OH" },

        ] as DrumHit[],
        [

            { type: "drum", timeStep: 0, note: "LT" },
            { type: "drum", timeStep: 2, note: "LT" },
            { type: "drum", timeStep: 14, note: "LT" },

            { type: "drum", timeStep: 3, note: "MT" },
            { type: "drum", timeStep: 8, note: "MT" },
            { type: "drum", timeStep: 10, note: "MT" },

            { type: "drum", timeStep: 1, note: "HT" },
            { type: "drum", timeStep: 4, note: "HT" },
            { type: "drum", timeStep: 6, note: "HT" },
            { type: "drum", timeStep: 9, note: "HT" },
            { type: "drum", timeStep: 11, note: "HT" },
            { type: "drum", timeStep: 13, note: "HT" },
            { type: "drum", timeStep: 15, note: "HT" }

        ] as DrumHit[],
        [

            { type: "drum", timeStep: 0, note: "CY" },
            { type: "drum", timeStep: 3, note: "CY" },
            { type: "drum", timeStep: 5, note: "CY" },
            { type: "drum", timeStep: 9, note: "CY" },
            { type: "drum", timeStep: 11, note: "CY" },
            { type: "drum", timeStep: 14, note: "CY" },

            { type: "drum", timeStep: 1, note: "OH" },
            { type: "drum", timeStep: 4, note: "OH" },
            { type: "drum", timeStep: 8, note: "OH" },
            { type: "drum", timeStep: 13, note: "OH" },

            { type: "drum", timeStep: 0, note: "CH" },
            { type: "drum", timeStep: 3, note: "CH" },
            { type: "drum", timeStep: 7, note: "CH" },
            { type: "drum", timeStep: 11, note: "CH" }

        ] as DrumHit[],
        [

            { type: "drum", timeStep: 0, note: "BD" },
            { type: "drum", timeStep: 6, note: "BD" },
            { type: "drum", timeStep: 9, note: "BD" },

            { type: "drum", timeStep: 0, note: "SD" },
            { type: "drum", timeStep: 11, note: "SD" },

            { type: "drum", timeStep: 3, note: "LT" },
            { type: "drum", timeStep: 8, note: "LT" },

            { type: "drum", timeStep: 2, note: "MT" },
            { type: "drum", timeStep: 7, note: "MT" },
            { type: "drum", timeStep: 14, note: "MT" },

            { type: "drum", timeStep: 1, note: "HT" },
            { type: "drum", timeStep: 8, note: "HT" },
            { type: "drum", timeStep: 9, note: "HT" },
            { type: "drum", timeStep: 14, note: "HT" },
            { type: "drum", timeStep: 15, note: "HT" },

            { type: "drum", timeStep: 1, note: "RS" },
            { type: "drum", timeStep: 2, note: "RS" },
            { type: "drum", timeStep: 3, note: "RS" },
            { type: "drum", timeStep: 4, note: "RS" },
            { type: "drum", timeStep: 6, note: "RS" },
            { type: "drum", timeStep: 7, note: "RS" },
            { type: "drum", timeStep: 8, note: "RS" },
            { type: "drum", timeStep: 9, note: "RS" },
            { type: "drum", timeStep: 10, note: "RS" },
            { type: "drum", timeStep: 11, note: "RS" },
            { type: "drum", timeStep: 12, note: "RS" },
            { type: "drum", timeStep: 14, note: "RS" },
            { type: "drum", timeStep: 15, note: "RS" },

            { type: "drum", timeStep: 0, note: "CP" },
            { type: "drum", timeStep: 1, note: "CP" },
            { type: "drum", timeStep: 2, note: "CP" },
            { type: "drum", timeStep: 3, note: "CP" },
            { type: "drum", timeStep: 4, note: "CP" },
            { type: "drum", timeStep: 5, note: "CP" },
            { type: "drum", timeStep: 8, note: "CP" },
            { type: "drum", timeStep: 9, note: "CP" },
            { type: "drum", timeStep: 10, note: "CP" },
            { type: "drum", timeStep: 11, note: "CP" },
            { type: "drum", timeStep: 12, note: "CP" },
            { type: "drum", timeStep: 14, note: "CP" },
            { type: "drum", timeStep: 15, note: "CP" },

            { type: "drum", timeStep: 0, note: "CB" },
            { type: "drum", timeStep: 1, note: "CB" },
            { type: "drum", timeStep: 3, note: "CB" },
            { type: "drum", timeStep: 6, note: "CB" },
            { type: "drum", timeStep: 8, note: "CB" },
            { type: "drum", timeStep: 12, note: "CB" },
            { type: "drum", timeStep: 15, note: "CB" },

            { type: "drum", timeStep: 0, note: "CY" },
            { type: "drum", timeStep: 6, note: "CY" },
            { type: "drum", timeStep: 10, note: "CY" },
            { type: "drum", timeStep: 14, note: "CY" },

            { type: "drum", timeStep: 2, note: "OH" },
            { type: "drum", timeStep: 8, note: "OH" },
            { type: "drum", timeStep: 14, note: "OH" },

            { type: "drum", timeStep: 1, note: "CH" },
            { type: "drum", timeStep: 4, note: "CH" },
            { type: "drum", timeStep: 6, note: "CH" },
            { type: "drum", timeStep: 9, note: "CH" },
            { type: "drum", timeStep: 14, note: "CH" }
        ] as DrumHit[],
        [

            { type: "drum", timeStep: 0, note: "BD" },

            { type: "drum", timeStep: 0, note: "SD" },
            { type: "drum", timeStep: 1, note: "SD" },
            { type: "drum", timeStep: 3, note: "SD" },
            { type: "drum", timeStep: 7, note: "SD" },
            { type: "drum", timeStep: 9, note: "SD" },
            { type: "drum", timeStep: 10, note: "SD" },
            { type: "drum", timeStep: 11, note: "SD" },
            { type: "drum", timeStep: 14, note: "SD" },
            { type: "drum", timeStep: 15, note: "SD" },

            { type: "drum", timeStep: 0, note: "CP" },
            { type: "drum", timeStep: 1, note: "CP" },
            { type: "drum", timeStep: 3, note: "CP" },
            { type: "drum", timeStep: 5, note: "CP" },
            { type: "drum", timeStep: 9, note: "CP" },
            { type: "drum", timeStep: 12, note: "CP" },
            { type: "drum", timeStep: 15, note: "CP" },

            { type: "drum", timeStep: 0, note: "OH" },
            { type: "drum", timeStep: 2, note: "OH" },
            { type: "drum", timeStep: 4, note: "OH" },
            { type: "drum", timeStep: 9, note: "OH" },
            { type: "drum", timeStep: 11, note: "OH" },
            { type: "drum", timeStep: 15, note: "OH" },

            { type: "drum", timeStep: 0, note: "CH" },
            { type: "drum", timeStep: 3, note: "CH" },
            { type: "drum", timeStep: 7, note: "CH" },
            { type: "drum", timeStep: 8, note: "CH" },
            { type: "drum", timeStep: 11, note: "CH" },
            { type: "drum", timeStep: 13, note: "CH" }

        ] as DrumHit[]
    ])!;
}